import React, { useRef, useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { FloorMapStudioEngine } from './FloorMapStudioEngine';
import {
  iFloorMapData,
  iFloorMapVersion,
} from 'services/FloorMap/FloorMap.type';
import { iExhibitorListRest } from 'services/Exhibitor/Exhibitor.type';
import { useHeaderSlot } from '../../contexts/HeaderSlotContext';
import routes from '../../router/routes';
import {
  FloorMapHistoryApi,
  FloorMapLayersApi,
  FloorMapStandRef,
} from './FloorMapStudio.types';
import { FloorMapHistoryModal, FloorMapPreviewBanner } from './FloorMapHistory';
import { FloorMapLayersModal } from './FloorMapLayers';
import s from './FloorMapStudio.module.scss';

const AUTO_SAVE_DELAY = 3000;

// ── Light-header button styles ────────────────────────────────────────────────
const ghost: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'rgba(17,24,39,0.65)',
  borderRadius: 6,
  padding: '5px 10px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  transition: 'all 0.15s',
  whiteSpace: 'nowrap',
};

const outline: React.CSSProperties = {
  ...ghost,
  border: '1px solid rgba(17,24,39,0.2)',
  color: 'rgba(17,24,39,0.85)',
};

const activeTabStyle: React.CSSProperties = {
  ...ghost,
  background: 'rgba(17,24,39,0.08)',
  border: '1px solid rgba(17,24,39,0.2)',
  color: '#111827',
};

const sep: React.CSSProperties = {
  width: 1,
  height: 20,
  background: 'rgba(17,24,39,0.12)',
  flexShrink: 0,
};

// ── FloorMap header controls ─────────────────────────────────────────────────
interface HeaderControlsProps {
  isDirty: boolean;
  saving: boolean;
  activePane: 'px' | 'vc';
  onBack: () => void;
  onSave: () => void;
  onSwitchPane: (t: 'px' | 'vc') => void;
  modBarRef: React.RefObject<HTMLDivElement>;
  previewing: boolean;
  onOpenHistory?: () => void;
  onOpenLayers?: () => void;
  isOnline: boolean;
  onToggleOnline: (value: boolean) => void;
}

function FloorMapHeaderControls({
  isDirty,
  saving,
  activePane,
  onBack,
  onSave,
  onSwitchPane,
  modBarRef,
  previewing,
  onOpenHistory,
  onOpenLayers,
  isOnline,
  onToggleOnline,
}: HeaderControlsProps): JSX.Element {
  const [exportOpen, setExportOpen] = useState(false);

  const call = (fn: string, ...args: unknown[]): void => {
    const f = (window as any)[fn];
    if (typeof f === 'function') f(...args);
  };

  const saveColor = previewing
    ? '#f59e0b'
    : saving
    ? '#f59e0b'
    : isDirty
    ? '#fca5a5'
    : '#86efac';
  const saveLabel = previewing
    ? '👁 Aperçu (lecture seule)'
    : saving
    ? '⏳ Sauvegarde…'
    : isDirty
    ? '● Non sauvegardé'
    : '✓ Sauvegardé';

  return (
    <>
      <button style={ghost} onClick={onBack} title="Retour à l'événement">
        ← Retour
      </button>

      <div style={sep} />

      <button
        style={activePane === 'px' ? activeTabStyle : ghost}
        onClick={() => onSwitchPane('px')}
        title="Mode Pixel Art"
      >
        <svg width="13" height="13" viewBox="0 0 20 20" fill="currentColor">
          <rect x="1" y="1" width="5" height="5" rx=".5" />
          <rect x="8" y="1" width="5" height="5" rx=".5" opacity=".35" />
          <rect x="15" y="1" width="5" height="5" rx=".5" />
          <rect x="1" y="8" width="5" height="5" rx=".5" opacity=".35" />
          <rect x="8" y="8" width="5" height="5" rx=".5" />
          <rect x="1" y="15" width="5" height="5" rx=".5" />
          <rect x="15" y="8" width="5" height="5" rx=".5" opacity=".35" />
          <rect x="8" y="15" width="5" height="5" rx=".5" opacity=".35" />
          <rect x="15" y="15" width="5" height="5" rx=".5" />
        </svg>
        Pixel Art
      </button>
      <button
        style={activePane === 'vc' ? activeTabStyle : ghost}
        onClick={() => onSwitchPane('vc')}
        title="Mode Vectoriel"
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <rect x="2" y="2" width="7" height="7" rx="1" />
          <rect x="11" y="2" width="7" height="7" rx="1" />
          <rect x="2" y="11" width="7" height="7" rx="1" />
          <rect x="11" y="11" width="7" height="7" rx="1" />
        </svg>
        Vectoriel
      </button>

      <div style={sep} />

      <button style={ghost} onClick={() => call('doUndo')} title="Annuler">
        <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
          <path d="M7 4l-5 5 5 5V9h6a4 4 0 010 8H7v2h6A6 6 0 0013 7H7V4z" />
        </svg>
        Annuler
      </button>

      <button style={ghost} onClick={() => call('openBgModal')}>
        <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
          <path d="M2 3a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H3a1 1 0 01-1-1V3zm2 1v9l3-3 3 3 3-4 3 4V4H4zm8 3a1 1 0 100-2 1 1 0 000 2z" />
        </svg>
        Image de fond
      </button>

      <div style={sep} />

      <button
        style={ghost}
        onClick={() => {
          call('syncPxToVc');
          onSwitchPane('vc');
        }}
        title="Pixel → Vectoriel"
      >
        Pixel → Vec
      </button>
      <button
        style={ghost}
        onClick={() => {
          try {
            call('syncVcToPx');
          } catch (e) {
            console.error('[FloorMapStudio] syncVcToPx error:', e);
          }
          onSwitchPane('px');
        }}
        title="Vectoriel → Pixel"
      >
        Vec → Pixel
      </button>

      <div style={sep} />

      <button style={ghost} onClick={() => call('openRtypesAdmin')}>
        <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
            clipRule="evenodd"
          />
        </svg>
        Paramètres
      </button>

      {/* Export — pure React dropdown */}
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <button
          style={ghost}
          onClick={() => setExportOpen((o) => !o)}
          onBlur={() => setTimeout(() => setExportOpen(false), 150)}
        >
          Export
          <svg width="10" height="10" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        {exportOpen && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              left: 0,
              background: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: 6,
              overflow: 'hidden',
              minWidth: 150,
              zIndex: 9999,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <div
              style={{
                padding: '4px 12px 3px',
                fontSize: 9,
                color: '#999',
                letterSpacing: '1.2px',
                textTransform: 'uppercase',
                fontWeight: 700,
              }}
            >
              Pixel Art
            </div>
            {([['px', 'png', '🖼 PNG']] as Array<[string, string, string]>).map(
              ([mode, fmt, label]) => (
                <ExportItem
                  key={`${mode}-${fmt}`}
                  label={label}
                  onSelect={() => {
                    call('doExport', mode, fmt);
                    setExportOpen(false);
                  }}
                />
              )
            )}
            <div
              style={{
                padding: '4px 12px 3px',
                fontSize: 9,
                color: '#999',
                letterSpacing: '1.2px',
                textTransform: 'uppercase',
                fontWeight: 700,
                borderTop: '1px solid #eee',
                marginTop: 2,
              }}
            >
              Vectoriel
            </div>
            {(
              [
                ['vc', 'png', '🖼 PNG'],
                ['vc', 'svg', '✦ SVG'],
                ['vc', 'pdf', '📄 PDF'],
              ] as Array<[string, string, string]>
            ).map(([mode, fmt, label]) => (
              <ExportItem
                key={`${mode}-${fmt}`}
                label={label}
                onSelect={() => {
                  call('doExport', mode, fmt);
                  setExportOpen(false);
                }}
              />
            ))}
          </div>
        )}
      </div>

      <div style={sep} />

      {/* Zoom controls */}
      <button
        style={{ ...ghost, padding: '4px 7px' }}
        onClick={() => call(activePane === 'px' ? 'pxZoomOut' : 'vcZoomOut')}
        title="Zoom out"
      >
        −
      </button>
      <button
        style={{ ...ghost, padding: '4px 7px' }}
        onClick={() => call(activePane === 'px' ? 'pxZoomIn' : 'vcZoomIn')}
        title="Zoom in"
      >
        +
      </button>
      <span
        id={activePane === 'px' ? 'px-zpct-hdr' : 'vc-zpct-hdr'}
        style={{ fontSize: 11, color: 'rgba(17,24,39,0.45)', minWidth: 32 }}
      />
      <button
        style={{ ...ghost, fontSize: 11, padding: '4px 7px' }}
        onClick={() => call(activePane === 'px' ? 'fitPx' : 'fitVc')}
        title="Fit"
      >
        Fit
      </button>

      <div style={sep} />

      {/* Mod-bar transplant target — engine populates #mod-bar by ID */}
      <div
        ref={modBarRef}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          flexShrink: 0,
          pointerEvents: previewing ? 'none' : undefined,
        }}
      />

      <div style={sep} />

      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: saveColor,
          minWidth: 110,
        }}
      >
        {saveLabel}
      </span>

      {onOpenHistory && (
        <button
          style={ghost}
          onClick={onOpenHistory}
          title="Historique des versions"
        >
          🕘 Historique
        </button>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 12,
          fontWeight: 600,
          opacity: previewing ? 0.5 : 1,
        }}
        title="Afficher le plan sur le site public"
      >
        <button
          type="button"
          role="switch"
          aria-checked={isOnline}
          aria-label="Online"
          disabled={previewing}
          onClick={() => onToggleOnline(!isOnline)}
          style={{
            position: 'relative',
            width: 36,
            height: 20,
            padding: 0,
            border: 'none',
            borderRadius: 10,
            background: isOnline ? '#22c55e' : '#d1d5db',
            cursor: previewing ? 'not-allowed' : 'pointer',
            transition: 'background 0.15s',
          }}
        >
          <span
            style={{
              position: 'absolute',
              top: 2,
              left: isOnline ? 18 : 2,
              width: 16,
              height: 16,
              borderRadius: '50%',
              background: '#fff',
              boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
              transition: 'left 0.15s',
            }}
          />
        </button>
        Online
      </div>

      <button
        style={{ ...outline, opacity: previewing ? 0.5 : 1 }}
        onClick={onSave}
        disabled={previewing}
        title={previewing ? "Quittez l'aperçu pour sauvegarder" : undefined}
      >
        💾 Sauvegarder
      </button>

      {onOpenLayers && (
        <button
          style={outline}
          onClick={onOpenLayers}
          title="Calques et légende du plan"
        >
          🎨 Calques
        </button>
      )}
    </>
  );
}

function ExportItem({
  label,
  onSelect,
}: {
  label: string;
  onSelect: () => void;
}): JSX.Element {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseDown={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '7px 14px',
        cursor: 'pointer',
        fontSize: 11,
        color: '#555',
        background: hovered ? '#f5f5f5' : '',
      }}
    >
      {label}
    </div>
  );
}

// ── Unified right sidebar — transplants engine DOM panels into React tabs ────
type SideTab = 'tools' | 'props';

interface UnifiedSidebarProps {
  toolsRef: React.RefObject<HTMLDivElement>;
  propsRef: React.RefObject<HTMLDivElement>;
  disabled?: boolean;
}

function UnifiedSidebar({
  toolsRef,
  propsRef,
  disabled,
}: UnifiedSidebarProps): JSX.Element {
  const [activeTab, setActiveTab] = useState<SideTab>('tools');

  const tabBtn = (tab: SideTab): React.CSSProperties => ({
    flex: 1,
    padding: '9px 0',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    color: activeTab === tab ? 'var(--sp-blue, #0d3b66)' : '#888',
    borderBottom:
      activeTab === tab
        ? '2px solid var(--sp-blue, #0d3b66)'
        : '2px solid transparent',
    transition: 'all 0.15s',
    fontFamily: 'inherit',
  });

  return (
    <div
      className={s.unifiedSidebar}
      style={disabled ? { pointerEvents: 'none' } : undefined}
    >
      {/* Tab bar */}
      <div className={s.sidebarTabBar}>
        <button style={tabBtn('tools')} onClick={() => setActiveTab('tools')}>
          Outils
        </button>
        <button style={tabBtn('props')} onClick={() => setActiveTab('props')}>
          Propriétés
        </button>
      </div>

      {/* Tools panel (engine .side — left) */}
      <div
        ref={toolsRef}
        className={s.sidebarPanel}
        style={{ display: activeTab === 'tools' ? 'flex' : 'none' }}
      />

      {/* Props panel (engine .side.r — right) */}
      <div
        ref={propsRef}
        className={s.sidebarPanel}
        style={{ display: activeTab === 'props' ? 'flex' : 'none' }}
      />
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
interface FloorMapStudioProps {
  initialData: iFloorMapData | null;
  exhibitors: iExhibitorListRest[];
  onSave: (data: Partial<iFloorMapData>) => Promise<void>;
  saving?: boolean;
  history?: FloorMapHistoryApi;
  layers?: FloorMapLayersApi;
  isOnline: boolean;
  onToggleOnline: (value: boolean) => void;
}

export function FloorMapStudio({
  initialData,
  exhibitors,
  onSave,
  saving,
  history,
  layers,
  isOnline,
  onToggleOnline,
}: FloorMapStudioProps): JSX.Element {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<FloorMapStudioEngine | null>(null);
  const toolsRef = useRef<HTMLDivElement>(null);
  const propsRef = useRef<HTMLDivElement>(null);
  const modBarRef = useRef<HTMLDivElement>(null);
  const pxToolPanelsRef = useRef<HTMLElement[]>([]);
  const vcToolPanelsRef = useRef<HTMLElement[]>([]);
  const pxPropPanelsRef = useRef<HTMLElement[]>([]);
  const vcPropPanelsRef = useRef<HTMLElement[]>([]);

  const [isDirty, setIsDirty] = useState(false);
  const [layersOpen, setLayersOpen] = useState(false);
  const [layerStands, setLayerStands] = useState<FloorMapStandRef[]>([]);
  const [activePane, setActivePane] = useState<'px' | 'vc'>('px');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [slotEl, setSlotEl] = useState<HTMLElement | null>(null);
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savingRef = useRef(false);
  const isDirtyRef = useRef(false);
  const onSaveRef = useRef(onSave);

  // Version preview: while set, the editor is read-only and nothing is saved
  const [preview, setPreview] = useState<iFloorMapVersion | null>(null);
  const previewRef = useRef<iFloorMapVersion | null>(null);
  // Live editor state kept aside while previewing, restored when leaving the preview
  const liveSnapshotRef = useRef<Partial<iFloorMapData> | null>(null);
  const liveDirtyRef = useRef(false);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyVersions, setHistoryVersions] = useState<iFloorMapVersion[]>(
    []
  );
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const { claimSlot, releaseSlot } = useHeaderSlot();
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();

  useEffect(() => {
    claimSlot();
    // Wait one rAF for Header to re-render with isSlotClaimed=true and create the slot div
    const raf = requestAnimationFrame(() => {
      setSlotEl(document.getElementById('app-header-center-slot'));
    });
    return () => {
      cancelAnimationFrame(raf);
      setSlotEl(null);
      releaseSlot();
    };
  }, [claimSlot, releaseSlot]);

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);
  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  useEffect(() => {
    if (!containerRef.current) return;

    const engine = new FloorMapStudioEngine();
    engine.init({
      container: containerRef.current,
      initialData,
      exhibitors,
      onDirtyChange: setIsDirty,
      onModeChange: setActivePane,
    });
    engineRef.current = engine;

    // Hide the engine's built-in header
    const hdr = containerRef.current.querySelector<HTMLElement>('.hdr');
    if (hdr) hdr.style.display = 'none';

    // Transplant #mod-bar content into React header target, then reuse same ID
    const engineModBar =
      containerRef.current.querySelector<HTMLElement>('#mod-bar');
    if (engineModBar && modBarRef.current) {
      while (engineModBar.firstChild) {
        modBarRef.current.appendChild(engineModBar.firstChild);
      }
      engineModBar.style.display = 'none';
      modBarRef.current.id = 'mod-bar'; // engine writes to this ID
    }

    // Transplant left .side panels: px panels visible, vc panels hidden
    const pxPane = containerRef.current.querySelector<HTMLElement>('#pane-px');
    const vcPane = containerRef.current.querySelector<HTMLElement>('#pane-vc');

    pxPane?.querySelectorAll<HTMLElement>('.side:not(.r)').forEach((panel) => {
      Object.assign(panel.style, {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        border: 'none',
        height: '100%',
        maxHeight: '100%',
      });
      pxToolPanelsRef.current.push(panel);
      toolsRef.current?.appendChild(panel);
    });

    vcPane?.querySelectorAll<HTMLElement>('.side:not(.r)').forEach((panel) => {
      Object.assign(panel.style, {
        display: 'none',
        flexDirection: 'column',
        width: '100%',
        border: 'none',
        height: '100%',
        maxHeight: '100%',
      });
      vcToolPanelsRef.current.push(panel);
      toolsRef.current?.appendChild(panel);
    });

    // Transplant right .side.r panels into Propriétés tab; hide zoom sec
    pxPane?.querySelectorAll<HTMLElement>('.side.r').forEach((panel) => {
      const zoomSec = panel.querySelector<HTMLElement>('.sec');
      if (zoomSec) zoomSec.style.display = 'none';
      Object.assign(panel.style, {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        border: 'none',
        height: '100%',
        maxHeight: '100%',
      });
      pxPropPanelsRef.current.push(panel);
      propsRef.current?.appendChild(panel);
    });

    vcPane?.querySelectorAll<HTMLElement>('.side.r').forEach((panel) => {
      const zoomSec = panel.querySelector<HTMLElement>('.sec');
      if (zoomSec) zoomSec.style.display = 'none';
      Object.assign(panel.style, {
        display: 'none',
        flexDirection: 'column',
        width: '100%',
        border: 'none',
        height: '100%',
        maxHeight: '100%',
      });
      vcPropPanelsRef.current.push(panel);
      propsRef.current?.appendChild(panel);
    });

    // Sync zoom % text from engine spans into header spans each frame
    let zoomRaf: number;
    const syncZoom = (): void => {
      const pxPct = document.getElementById('px-zpct');
      const vcPct = document.getElementById('vc-zpct');
      const pxHdr = document.getElementById('px-zpct-hdr');
      const vcHdr = document.getElementById('vc-zpct-hdr');
      if (pxPct && pxHdr && pxHdr.textContent !== pxPct.textContent)
        pxHdr.textContent = pxPct.textContent;
      if (vcPct && vcHdr && vcHdr.textContent !== vcPct.textContent)
        vcHdr.textContent = vcPct.textContent;
      zoomRaf = requestAnimationFrame(syncZoom);
    };
    zoomRaf = requestAnimationFrame(syncZoom);

    // Re-centre canvases after layout settles
    setTimeout(() => {
      const fitPx = (window as any).fitPx;
      const fitVc = (window as any).fitVc;
      if (typeof fitPx === 'function') fitPx();
      if (typeof fitVc === 'function') fitVc();
      engineRef.current?.resize();
    }, 120);

    return () => {
      cancelAnimationFrame(zoomRaf);
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = null;
      }
      if (isDirtyRef.current && !previewRef.current) {
        const serialized = engine.serialize();
        onSaveRef.current(serialized);
      }
      engine.destroy();
      engineRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (engineRef.current) {
      engineRef.current.updateExhibitors(exhibitors);
    }
  }, [exhibitors]);

  useEffect(() => {
    const isPx = activePane === 'px';
    pxToolPanelsRef.current.forEach((p) => {
      p.style.display = isPx ? 'flex' : 'none';
    });
    vcToolPanelsRef.current.forEach((p) => {
      p.style.display = isPx ? 'none' : 'flex';
    });
    pxPropPanelsRef.current.forEach((p) => {
      p.style.display = isPx ? 'flex' : 'none';
    });
    vcPropPanelsRef.current.forEach((p) => {
      p.style.display = isPx ? 'none' : 'flex';
    });
  }, [activePane]);

  useEffect(() => {
    const onFsChange = (): void => {
      setIsFullscreen(!!document.fullscreenElement);
      setTimeout(() => {
        engineRef.current?.resize();
        setTimeout(() => engineRef.current?.resize(), 150);
      }, 50);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(() => engineRef.current?.resize());
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = (): void => {
      if (!wrapperRef.current) return;
      const rect = wrapperRef.current.getBoundingClientRect();
      const header = document.getElementById('app-header');
      const headerH = header ? header.offsetHeight : 0;
      document.documentElement.classList.toggle(
        'floormap-active',
        rect.top <= headerH && rect.bottom > 0
      );
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.documentElement.classList.remove('floormap-active');
    };
  }, []);

  const toggleFullscreen = useCallback((): void => {
    if (!wrapperRef.current) return;
    if (!document.fullscreenElement) {
      wrapperRef.current.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!engineRef.current || savingRef.current || previewRef.current) return;
    savingRef.current = true;
    const serialized = engineRef.current.serialize();
    await onSave(serialized);
    setIsDirty(false);
    savingRef.current = false;
  }, [onSave]);

  const openHistory = useCallback(async (): Promise<void> => {
    if (!history) return;
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const forkerId = engineRef.current?.getActiveForkerId() ?? undefined;
      setHistoryVersions(await history.listVersions(forkerId));
    } catch (err: any) {
      setHistoryError(err.message);
    } finally {
      setHistoryLoading(false);
    }
  }, [history]);

  const previewVersion = useCallback(
    async (version: iFloorMapVersion): Promise<void> => {
      if (!history || !engineRef.current) return;
      setHistoryBusy(true);
      setHistoryError(null);
      try {
        // Entering the preview from the editor: flush pending changes first, then keep the live state
        if (!previewRef.current) {
          liveDirtyRef.current = isDirtyRef.current;
          if (isDirtyRef.current) await handleSave();
          liveSnapshotRef.current = engineRef.current.serialize();
        }
        const data = await history.getVersion(version.id);
        engineRef.current?.loadData(data);
        previewRef.current = version;
        setPreview(version);
        setHistoryOpen(false);
      } catch (err: any) {
        setHistoryError(err.message);
      } finally {
        setHistoryBusy(false);
      }
    },
    [history, handleSave]
  );

  const exitPreview = useCallback((): void => {
    const live = liveSnapshotRef.current;
    const liveDirty = liveDirtyRef.current;
    previewRef.current = null;
    liveSnapshotRef.current = null;
    liveDirtyRef.current = false;
    setPreview(null);
    if (live && engineRef.current) {
      engineRef.current.loadData(live as iFloorMapData);
      // Unsaved changes from before the preview are kept and will autosave
      if (liveDirty) setIsDirty(true);
    }
  }, []);

  const restoreVersion = useCallback(
    async (version: iFloorMapVersion): Promise<void> => {
      if (!history) return;
      const ok = window.confirm(
        `Restaurer la version v${version.version} ?\nUne nouvelle version sera créée à partir de celle-ci.`
      );
      if (!ok) return;
      setHistoryBusy(true);
      setHistoryError(null);
      try {
        const res = await history.restoreVersion(version.id);
        previewRef.current = null;
        liveSnapshotRef.current = null;
        liveDirtyRef.current = false;
        setPreview(null);
        setHistoryOpen(false);
        engineRef.current?.loadData(res.floor_map);
      } catch (err: any) {
        setHistoryError(err.message);
      } finally {
        setHistoryBusy(false);
      }
    },
    [history]
  );

  useEffect(() => {
    if (isDirty) {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
      autoSaveTimer.current = setTimeout(handleSave, AUTO_SAVE_DELAY);
    }
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [isDirty, handleSave]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent): void => {
      if (isDirty && !preview) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, preview]);

  const handleBack = useCallback((): void => {
    if (eventId) {
      navigate(routes.home2.path + `events/${eventId}`);
    } else {
      navigate(-1);
    }
  }, [eventId, navigate]);

  const openLayers = useCallback((): void => {
    setLayerStands(engineRef.current?.getStands() ?? []);
    setLayersOpen(true);
  }, []);

  const handleSwitchPane = useCallback((t: 'px' | 'vc'): void => {
    setActivePane(t);
    const f = (window as any).switchTab;
    if (typeof f === 'function') f(t);
  }, []);

  return (
    <div
      ref={wrapperRef}
      className={`${s.studioWrapper} ${isFullscreen ? s.fullscreen : ''}`}
    >
      {slotEl &&
        ReactDOM.createPortal(
          <FloorMapHeaderControls
            isDirty={isDirty}
            saving={saving ?? false}
            activePane={activePane}
            onBack={handleBack}
            onSave={handleSave}
            onSwitchPane={handleSwitchPane}
            modBarRef={modBarRef}
            previewing={!!preview}
            onOpenHistory={history ? openHistory : undefined}
            onOpenLayers={layers ? openLayers : undefined}
            isOnline={isOnline}
            onToggleOnline={onToggleOnline}
          />,
          slotEl
        )}

      {preview && (
        <FloorMapPreviewBanner
          version={preview}
          busy={historyBusy}
          onRestore={async () => await restoreVersion(preview)}
          onExit={exitPreview}
        />
      )}

      {historyOpen &&
        wrapperRef.current &&
        ReactDOM.createPortal(
          <FloorMapHistoryModal
            versions={historyVersions}
            loading={historyLoading}
            error={historyError}
            busy={historyBusy}
            onPreview={previewVersion}
            onRestore={restoreVersion}
            onClose={() => setHistoryOpen(false)}
          />,
          wrapperRef.current
        )}

      {layersOpen &&
        layers &&
        wrapperRef.current &&
        ReactDOM.createPortal(
          <FloorMapLayersModal
            api={layers}
            stands={layerStands}
            onClose={() => setLayersOpen(false)}
          />,
          wrapperRef.current
        )}

      {/* Fullscreen toggle */}
      <div className={s.floatingToolbar}>
        <button className={s.toolbarBtn} onClick={toggleFullscreen}>
          {isFullscreen ? (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M4 14h6v6m10-10h-6V4M14 10l7-7M3 21l7-7" />
            </svg>
          ) : (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
            </svg>
          )}
        </button>
      </div>

      {/* Engine canvas area (sidebars are transplanted out of here) */}
      <div
        ref={containerRef}
        className={s.engineContainer}
        style={preview ? { pointerEvents: 'none' } : undefined}
      />

      {/* Unified right sidebar */}
      <UnifiedSidebar
        toolsRef={toolsRef}
        propsRef={propsRef}
        disabled={!!preview}
      />
    </div>
  );
}
